import { component } from "picoapp";
import { type Context } from "../../state";

const getOffsetValue = () => {
  const announcementBarHeight =
    document
      .querySelector("#shopify-section-tbsp-announcement-bar")
      ?.getBoundingClientRect?.().height ?? 0;
  const topNavigationHeight =
    document
      .querySelector("#shopify-section-tbsp-navigation")
      ?.getBoundingClientRect?.().height ?? 0;

  return announcementBarHeight + topNavigationHeight;
};

const setupIntersetctionObserver = ({
  boxShadowClass,
  isDesktopEnabled,
  isMobileEnabled,
  lastAnchorElement,
  node,
}: {
  boxShadowClass: string;
  isDesktopEnabled: boolean;
  isMobileEnabled: boolean;
  lastAnchorElement: HTMLElement;
  node: HTMLElement;
}) => {
  const offset = getOffsetValue();

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.rootBounds) {
          return;
        }

        if (!entry.isIntersecting && isMobileEnabled) {
          node.classList.replace("translate-y-full", "translate-y-0");
        }
        if (entry.isIntersecting && isMobileEnabled) {
          node.classList.replace("translate-y-0", "translate-y-full");
        }
        if (!entry.isIntersecting && isDesktopEnabled) {
          node.classList.replace("mg:-translate-y-full", "mg:translate-y-0");
          node.classList.replace("mg:shadow-none", `mg:${boxShadowClass}`);
        }
        if (entry.isIntersecting && isDesktopEnabled) {
          node.classList.replace("mg:translate-y-0", "mg:-translate-y-full");
          node.classList.replace(`mg:${boxShadowClass}`, "mg:shadow-none");
        }
      });
    },
    {
      rootMargin: `-${offset}px 0px 0px`,
    }
  );

  intersectionObserver.observe(lastAnchorElement);

  return intersectionObserver;
};

export const stickyProductBar = component<Context>((node) => {
  let intersectionObserver: IntersectionObserver;
  const rootElement = node.closest<HTMLElement>(`[id$="root"]`);

  if (!rootElement) {
    return;
  }

  // Make the bar a child of our root element
  // This ensures sticky positioning in the correct hierarchy
  rootElement.prepend(node);
  node.classList.replace("hidden", "block");
  node.classList.remove(
    "mg:shadow-dark-product-bar",
    "mg:shadow-light-product-bar"
  );

  const isMobileEnabled = node.classList.contains("mobile-sticky-enabled");
  const isDesktopEnabled = node.classList.contains("desktop-sticky-enabled");

  const anchorInput = node.dataset.anchorSelector;
  // If no anchor element, we don't need intersection observer and we should show this at all times
  if (!anchorInput && isMobileEnabled) {
    node.classList.replace("translate-y-full", "translate-y-0");
    return;
  }

  const boxShadowClass = `shadow-${node.dataset.theme ?? "light"}-product-bar`;

  if (!anchorInput && isDesktopEnabled) {
    node.classList.replace("mg:-translate-y-full", "mg:translate-y-0");
    node.classList.replace("mg:shadow-none", `mg:${boxShadowClass}`);
  }

  if (!anchorInput) return;

  // Find the last element in the DOM that matches the selector
  // This helps use cases like Optimizely hiding CTAs
  const [lastAnchorElement] = [
    ...document.querySelectorAll<HTMLElement>(anchorInput),
  ].reverse();

  if (lastAnchorElement) {
    intersectionObserver = setupIntersetctionObserver({
      boxShadowClass,
      isDesktopEnabled,
      isMobileEnabled,
      lastAnchorElement,
      node,
    });
  }

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.target.nodeName === "BUTTON") {
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }

        intersectionObserver = setupIntersetctionObserver({
          boxShadowClass,
          isDesktopEnabled,
          isMobileEnabled,
          lastAnchorElement: mutation.target as HTMLElement,
          node,
        });
      }
    });
  });

  const productDetailsElement = document.querySelector("#product-details");
  if (productDetailsElement) {
    mutationObserver.observe(productDetailsElement, {
      childList: true,
      subtree: true,
    });
  }

  return () => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    if (mutationObserver) {
      mutationObserver.disconnect();
    }
  };
});
